import { View, SxProp, useDripsyTheme } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { formatAmountByDecimal } from '../../utils';
import { TokenPagination } from '@/design-system/components/pagination/TokenPagination';
import { TableView } from '@/design-system/components/table/TableView';
import { H3, TextBody } from '@/design-system/components/typography';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { UniInput } from '@/design-system/components/inputs';
import { UniButton } from '@/design-system/components/buttons';
import { CurrencySelector } from '@/design-system/components/currencySelector/CurrencySelector';
import { UniIconButton } from '@/design-system/components/buttons';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { CurrenciesValues } from '@/features/account-management/options';
import { PageContainer } from '@/features/account-management/screens/page/PageContainer';
import { useListAccounts } from '@/features/account-management/screens/list/hooks/useListAccounts.hook';
import { UiAccountResponse } from '@utx/utx-access-client';
import { AccountDetailsTabRoutes } from '@/features/account-management/screens/details/type';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';

export interface AccountListProps {}

export const AccountListWebScreen: React.FC<AccountListProps> = () => {
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { theme } = useDripsyTheme();
  const { t } = useTranslation('account-management');

  const [currency, setCurrency] = useState('HKD');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [actionNew, setActionNew] = useState<boolean>(false);

  const { accounts, isLoading, nextToken, preToken, next, prev, refetch, clearCurrentPageToken, createAccount } = useListAccounts({
    search: search,
    pageSize: 10
  });

  const onAccountPressed = (account: any, tab?: number) => {
    navigation.navigate(RootStackRoutes.AccountManagement, {
      screen: AccountManagementStackRoutes.Details,
      params: {
        screen: tab == 1 ? AccountDetailsTabRoutes.Card : AccountDetailsTabRoutes.Account,
        account: tab == 1 ? account?.cardAccountId : account?.id,
        record: account,
        tab: tab
      }
    });
  };

  return (
    <PageContainer sx={{ paddingX: '$10', paddingY: '$10' }}>
      <View sx={styles.container}>
        <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <H3 bold>{t('card-account-management::account-page.title--account-management')}</H3>
          <UniButton
            wrapperSx={{ width: 240 }}
            labelSx={{ color: '$black' }}
            label={t('card-account-management::account-page.button--download-transaction-report')}
            onPress={() =>
              navigationAny.navigate(RootStackRoutes.AccountManagement, {
                screen: AccountManagementStackRoutes.DownloadTransactionReport,
                params: {
                  screen: DownloadTransactionReportStackRoutes.DownloadAccountTransactionReport
                }
              })
            }
          />
        </View>
        <TextBody semiBold secondary sx={{ mt: '$5' }}>
          {t('card-account-management::account-page.text--select-account-for-details')}
        </TextBody>
        <PageItem sx={styles.card}>
          <View sx={styles.table}>
            <View sx={styles.operations}>
              <View sx={styles.filters}>
                <UniInput
                  wrapperSx={styles.filter_search}
                  size="default"
                  prefixIcon="search"
                  placeholder={t('card-account-management::account-page.text--search-by-account-name-no')}
                  onValueChange={(value) => {
                    clearCurrentPageToken();
                    setSearch(value);
                  }}
                />
                <CurrencySelector options={CurrenciesValues} onValueChange={(value) => setCurrency(value)} />
              </View>
              <UniButton
                wrapperSx={styles.btn_new}
                labelSx={{ color: '$black' }}
                label={t('card-account-management::account-page.button--new-account')}
                onPress={() =>
                  navigation.navigate(RootStackRoutes.AccountManagement, {
                    screen: AccountManagementStackRoutes.NewAccount,
                    params: {
                      func: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) =>
                        createAccount(data, onSuccess, onError),
                      refresh: refetch
                    }
                  })
                }
              />
            </View>
            <TableView<UiAccountResponse>
              data={accounts}
              loading={isLoading}
              rowVerticalAlign="center"
              columns={[
                {
                  title: t('card-account-management::account-page.text--account-name'),
                  index: 'name',
                  flex: 1,
                  render: (item) => <TextBody semiBold>{item.name}</TextBody>
                },
                {
                  title: t('card-account-management::account-page.text--account-number'),
                  index: 'accountNumber',
                  width: 200
                },
                {
                  title: t('card-account-management::account-page.text--value'),
                  index: 'valueHkd',
                  align: 'right',
                  render: (item) => (
                    <TextBody semiBold sx={{ color: theme?.colors?.$secondary as string }}>
                      {formatAmountByDecimal({
                        value: currency === 'HKD' ? item.valueHkd! : item.valueUsd!,
                        suffix: currency,
                        decimals: 2
                      })}
                    </TextBody>
                  )
                },
                {
                  title: t('card-account-management::account-page.text--go-to'),
                  index: 'id',
                  width: 200,
                  sx: { marginLeft: '$8' },
                  render: (item) => (
                    <View sx={{ flexDirection: 'row', gap: '$8' }}>
                      <UniIconButton icon="accountCenterWeb" onPress={(e) => onAccountPressed(item)} />
                      {item?.cardAccountId && <UniIconButton icon="cardWeb" onPress={(e) => onAccountPressed(item, 1)} />}
                    </View>
                  )
                }
              ]}
            />
          </View>
          <View sx={{ alignItems: 'flex-end' }}>
            <TokenPagination prevPageToken={preToken} nextPageToken={nextToken} onNextPage={next} onPrevPage={prev} />
          </View>
        </PageItem>
      </View>
    </PageContainer>
  );
};

enum StyleName {
  container = 'container',
  card = 'card',
  table = 'table',
  operations = 'operations',
  filters = 'filters',
  filter_search = 'filter_search',
  filter_status = 'filter_status',
  btn_new = 'btn_new'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flexDirection: 'column',
    height: '100%'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '$8',
    marginTop: '$8',
    gap: '$8',
    borderRadius: 15
  },
  table: {
    flexDirection: 'column',
    gap: '$8'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5'
  },
  filter_search: {
    minWidth: 400
  },
  filter_status: {
    width: '33%'
  },
  btn_new: {
    width: 200
  }
};
